import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';
import shared from '../sharedStyles';
import useIsMobile from '../hooks/useIsMobile';
import PageMeta from '../components/PageMeta';
import { isFreeProgram, ymcaNameOf } from '../utils/programType';
import useAppConfig from '../hooks/useAppConfig';
import SmsConsent, { smsConsentPayload } from '../components/SmsConsent';

import HelpTip from '../components/HelpTip';
import { errorMessage } from '../utils/offline';
const ParentRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginParent } = useAuth();
  const isMobile = useIsMobile();
  const styles = isMobile ? mobileStyles(baseStyles) : baseStyles;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [program, setProgram] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);
  const [programCode, setProgramCode] = useState(searchParams.get('code') || '');
  const { features } = useAppConfig();

  const [form, setForm] = useState({
    parentName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    smsOptIn: false, // A2P: the parent ticks the box; it is never pre-checked
  });

  useEffect(() => {
    if (programCode) {
      lookupProgram(programCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookupProgram = async (input) => {
    setProgramLoading(true);
    setError('');
    try {
      const res = await authAPI.lookupProgram(input);
      setProgram(res.data.program);
    } catch (err) {
      setError(errorMessage(err, 'No team found with that code or phone number. Please double-check or ask your coach.'));
      setProgram(null);
    } finally {
      setProgramLoading(false);
    }
  };

  const handleLookup = (e) => {
    e.preventDefault();
    if (programCode.trim()) lookupProgram(programCode.trim());
  };

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!program) {
      setError('Please enter a valid program code first.');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.parentRegister({
        ...form,
        // X-4: consent only reflects the box when it (and its disclosure) was shown.
        ...smsConsentPayload(!!features.sms, form.smsOptIn),
        programId: program.id,
      });
      const { token, family } = res.data;
      loginParent(token, family);
      navigate('/parent/dashboard');
    } catch (err) {
      setError(errorMessage(err, 'Registration failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <PageMeta
        title="Join Your Child's Basketball Program | HoopHero"
        description="Create a free HoopHero parent account to join your child's basketball program. No subscription, no fees — parent accounts are always free."
        path="/parent/register"
      />
      <div style={styles.box}>
        <img src="/logo-wide.png" alt="HoopHero" style={styles.logoImg} />
        <h1 style={{ margin: 0, fontWeight: 400, ...styles.tagline }}>Create your parent account</h1>

        {!program ? (
          <div>
            <div style={styles.infoBox}>
              <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#1A5FD4', marginBottom: '4px' }}>Find Your Team</div>
              <div style={{ fontSize: '13px', color: '#5A6070' }}>Ask your coach for your team code, or enter their phone number — either works.</div>
            </div>
            <form onSubmit={handleLookup} style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
              <div>
                <label style={{ ...shared.label, display: 'inline-block' }} htmlFor="pr-code">Team Code or Coach's Phone</label><HelpTip id="parent.register.teamCode" />
                <input
                  id="pr-code"
                  style={shared.input}
                  placeholder="yuleebombers123 or (904) 555-0100"
                  value={programCode}
                  onChange={e => setProgramCode(e.target.value)}
                  required
                />
              </div>
              {error && <div style={styles.error}>{error}</div>}
              <button type="submit" style={styles.btnBlue} disabled={programLoading}>
                {programLoading ? 'Looking up team...' : 'Find My Team'}
              </button>
            </form>
          </div>
        ) : (
          <div>
            <div style={styles.programBanner}>
              <div style={{ fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: '600', marginBottom: '3px' }}>Joining Team</div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '18px', fontWeight: '800', color: '#fff', marginBottom: '3px' }}>{program.name}</div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{program.season} · {program.age_group} · {program.location}</div>
              {program.program_type === 'free_ymca' && ymcaNameOf(program) && (
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', marginTop: '4px' }}>🏢 Hosted at {ymcaNameOf(program)}</div>
              )}
              {isFreeProgram(program.program_type) && (
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>Free program — no fees to pay.</div>
              )}
              <button
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer', marginTop: '8px', padding: 0 }}
                onClick={() => { setProgram(null); setProgramCode(''); }}
              >
                Change team
              </button>
            </div>

            {program.registration_open === false ? (
              <div style={{ background: '#FFF4E8', border: '1px solid rgba(240,90,20,0.25)', borderRadius: '8px', padding: '14px 16px', fontSize: '13.5px', color: '#5A6070', lineHeight: '1.6' }}>
                <strong style={{ color: '#0D1117' }}>{program.name}</strong> is not accepting new registrations right now.
                Please contact your coach to be enrolled.
              </div>
            ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
              <div style={styles.sectionLabel}>Your Information</div>
              <div>
                <label style={shared.label} htmlFor="pr-name">Parent / Guardian Name(s)</label>
                <input id="pr-name" autoComplete="name" style={shared.input} placeholder="Alex & Jamie Smith" value={form.parentName} onChange={e => update('parentName', e.target.value)} required />
              </div>
              <div style={styles.grid2}>
                <div>
                  <label style={shared.label} htmlFor="pr-email">Email Address</label>
                  <input id="pr-email" autoComplete="email" style={shared.input} type="email" placeholder="parent@email.com" value={form.email} onChange={e => update('email', e.target.value)} required />
                </div>
                <div>
                  <label style={shared.label} htmlFor="pr-phone">Phone Number</label>
                  <input id="pr-phone" type="tel" autoComplete="tel" style={shared.input} placeholder="(904) 555-0100" value={form.phone} onChange={e => update('phone', e.target.value)} />
                </div>
                <div>
                  <label style={shared.label} htmlFor="pr-password">Password</label>
                  <input id="pr-password" autoComplete="new-password" style={shared.input} type="password" placeholder="Min 8 characters" value={form.password} onChange={e => update('password', e.target.value)} required />
                </div>
                <div>
                  <label style={shared.label} htmlFor="pr-confirm">Confirm Password</label>
                  <input id="pr-confirm" autoComplete="new-password" style={shared.input} type="password" placeholder="Confirm password" value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} required />
                </div>
              </div>
              {features.sms && (
                <SmsConsent checked={form.smsOptIn} onChange={(v) => update('smsOptIn', v)} />
              )}
              <div style={{ background: '#F6F7FA', borderRadius: '8px', padding: '10px 12px', fontSize: '12.5px', color: '#5A6070' }}>
                After registering you can add your children from the My Children page.
              </div>
              {error && <div style={styles.error}>{error}</div>}
              <button type="submit" style={styles.btnBlue} disabled={loading}>
                {loading ? 'Creating account...' : 'Create Parent Account'}
              </button>
              <div style={{ fontSize: '11.5px', color: '#9AA0B0', textAlign: 'center', marginTop: '10px', lineHeight: '1.5' }}>
                By creating an account, you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#1A5FD4', textDecoration: 'underline' }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#1A5FD4', textDecoration: 'underline' }}>Privacy Policy</a>.
              </div>
            </form>
            )}
          </div>
        )}

        <div style={styles.footer}>
          Already have an account? <button style={styles.link} onClick={() => navigate('/parent/login')}>Sign in</button>
          <span style={{ margin: '0 8px', color: '#ECEEF3' }}>|</span>
          <button style={{ ...styles.link, color: '#5A6070' }} onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    </div>
  );
};

const baseStyles = {
  container: { minHeight: '100vh', background: '#F6F7FA', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  box: { background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '36px 40px', width: '100%', maxWidth: '480px' },
  logo: { fontFamily: 'Georgia, serif', fontSize: '28px', fontWeight: '900', textAlign: 'center', marginBottom: '4px', color: '#0D1117' },
  logoImg: { display: 'block', width: '300px', maxWidth: '100%', height: 'auto', margin: '0 auto 10px' },
  accent: { color: '#C2460B' },
  tagline: { textAlign: 'center', fontSize: '14px', color: '#5A6070', marginBottom: '24px' },
  infoBox: { background: '#E6EFFC', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' },
  programBanner: { background: '#1A3560', borderRadius: '8px', padding: '14px 16px', marginBottom: '18px' },
  error: { background: '#FDEAEA', color: '#C93535', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' },
  sectionLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#9AA0B0' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  btnBlue: { background: '#1A5FD4', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', width: '100%', fontFamily: 'inherit' },
  footer: { textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#5A6070' },
  link: { background: 'none', border: 'none', color: '#C2460B', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
};

const mobileStyles = (s) => ({
  ...s,
  box: { ...s.box, padding: '22px 20px' },
  logo: { ...s.logo, fontSize: '24px' },
  tagline: { ...s.tagline, fontSize: '13px', marginBottom: '20px' },
  // Stack the email/phone/password/confirm pairs vertically on mobile.
  grid2: { ...s.grid2, gridTemplateColumns: '1fr', gap: '11px' },
});

export default ParentRegister;